import { CancelMinor, InviteMinor, ReplayMinor } from '@shopify/polaris-icons'
import React, { Fragment, memo, useContext, useMemo } from 'react'
import tw from 'twin.macro'
import { ActionButton } from 'src/components/shared/ActionButton'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { OpportunityCandidateStatus } from 'src/constants/opportunity/enums'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { MenuActionItem } from 'src/interfaces/ui'
import { colors } from 'src/theme'
import { addS } from 'src/utils/baseUtils'
import { CandidatePoolTableContext } from './CandidatePoolTable.context'

/**
 * Button and menu live together: unlike the row kebab there is only ever one of these, and the menu's contents are
 * derived entirely from the same selection the button's disabled state reads.
 */
const CandidateBatchActionButtonComponent = () => {
  const { hasPermission } = useCheckPermissions()

  const {
    selectedTab,
    selectedCandidates,
    batchAnchor,
    openBatchAction,
    closeBatchAction,
    openOfferForCandidates,
    archiveCandidates,
    unarchiveCandidates,
  } = useContext(CandidatePoolTableContext)

  const canOffer = hasPermission(UserPermission.OpportunityCandidatesOffer)
  const canEdit = hasPermission(UserPermission.OpportunityCandidatesEdit)

  const actionItems: MenuActionItem[] = useMemo(() => {
    if (!selectedCandidates.length) {
      return []
    }

    const candidateIds = selectedCandidates.map((candidate) => {
      return candidate.id
    })

    const items: MenuActionItem[] = []

    if (selectedTab === OpportunityCandidateStatus.New) {
      if (canOffer) {
        items.push({
          label: `Offer onboarding to ${candidateIds.length} ${addS(
            candidateIds.length,
            'candidate',
          )}`,
          icon: <InviteMinor width={20} height={20} />,
          dataTestId: 'candidate-pool-batch-offer',
          onClick: () => {
            openOfferForCandidates(selectedCandidates)
            closeBatchAction()
          },
        })
      }

      if (canEdit) {
        items.push({
          label: 'Archive',
          icon: <CancelMinor width={20} height={20} fill={colors.COLORS_RED_500} />,
          color: colors.COLORS_RED_500,
          dataTestId: 'candidate-pool-batch-archive',
          onClick: () => {
            archiveCandidates(candidateIds)
            closeBatchAction()
          },
        })
      }
    }

    if (selectedTab === OpportunityCandidateStatus.Archived && canEdit) {
      items.push({
        label: 'Move back to New',
        icon: <ReplayMinor width={20} height={20} />,
        dataTestId: 'candidate-pool-batch-unarchive',
        onClick: () => {
          unarchiveCandidates(candidateIds)
          closeBatchAction()
        },
      })
    }

    return items
  }, [
    selectedCandidates,
    selectedTab,
    canOffer,
    canEdit,
    openOfferForCandidates,
    archiveCandidates,
    unarchiveCandidates,
    closeBatchAction,
  ])

  if (!canOffer && !canEdit) {
    return null
  }

  return (
    <Fragment>
      <ActionButton
        className={tw`px-4`}
        color="white"
        text="Actions"
        selected={Boolean(batchAnchor)}
        disabled={!selectedCandidates.length}
        onClick={openBatchAction}
        data-testid="candidate-pool-batch-actions-menu-btn"
      />
      <ActionMenu
        anchorEl={batchAnchor}
        itemClassName={tw`min-w-[208px]`}
        isOpen={Boolean(batchAnchor)}
        items={actionItems}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={closeBatchAction}
      />
    </Fragment>
  )
}

export const CandidateBatchActionButton = memo(CandidateBatchActionButtonComponent)
